import {useEffect, useRef, useState} from "react";
import {Box, Popover, TextField} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DoorFrontOutlinedIcon from "@mui/icons-material/DoorFrontOutlined";
import {useNavigate} from "react-router-dom";
import {useConfirm} from "../../../../../../misc/Confirm";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {RibbonButton, RibbonGroup, RibbonSelect, RibbonStack} from "../Ribbon";
import {findItem, removeDoorSlot, renameDoorSlot} from "../../scenes/frameGrid/BlockOps";
import {slotOfCell} from "../../model/frame/MdFrameSpec";
import {deselect, emitChanged, emitRebuild} from "./helpers";

type DoorSlotSel = Extract<MdSelection, {type: "door-slot"}>;

const NO_OPENING = "none";

// the contextual tab for a secondary door slot (multi-opening screens): its name, the opening
// that fills it, its width (an empty slot's design width), and Remove Slot. The primary slot's name is edited here too, since
// the primary's door cell draws the door and isn't selectable.
export function DoorSlotSelection(props: {app: ElevationApp; selection: DoorSlotSel[]}) {
    const app = props.app;
    const spec = app.frameSpec;
    const id = props.selection[0].id;

    useEventEmitter2(spec.frameBlocksChanged);
    useEventEmitter2(app.pendingSlotLinks);
    const slotOpenings = useEventEmitter2(app.slotOpenings);

    const slot = slotOfCell(spec, id);
    const item = findItem(spec, id);

    const [widthAnchor, setWidthAnchor] = useState<HTMLElement | null>(null);
    const widthRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const confirm = useConfirm();

    if(!slot || item?.kind !== "doors") return null;

    const opening = app.slotOpening(slot.name);
    const linked = slotOpenings?.linked.find(o => o.slot === slot.name) ?? null;
    // a saved slot keeps its name: openings hold it by name.
    const savedNames = app.savedSlotNames();

    // openings picked for the other slots since the last save can't fill this one too.
    const pickedElsewhere = new Set(Array.from(app.pendingSlotLinks.value.entries())
        .filter(([name]) => name !== slot.name)
        .map(([, o]) => o.id));
    const choices = [
        ...(linked ? [linked] : []),
        ...(slotOpenings?.candidates ?? []).filter(o => !pickedElsewhere.has(o.id)),
    ];
    const openingOptions = [
        ...(opening ? [] : [{value: NO_OPENING, label: "Pick an opening"}]),
        ...choices.map(o => ({value: o.id.toString(), label: o.hasScreen ? `${o.name} (unlinks its screen)` : o.name})),
    ];

    const pickOpening = (value: string) => {
        const picked = choices.find(o => o.id.toString() === value);
        if(picked) app.pickSlotOpening(slot.name, picked);
    };

    const rename = (from: string, to: string) => {
        try {
            renameDoorSlot(spec, from, to);
        } catch(e: any) {
            app.draw.showError(e.message);
            return;
        }

        // an opening picked for the slot stays with it under the new name.
        const picked = app.pendingSlotLinks.value.get(from);
        if(picked) {
            const picks = new Map(app.pendingSlotLinks.value);
            picks.delete(from);
            picks.set(to.trim(), picked);
            app.pendingSlotLinks.emit(picks);
        }

        emitChanged(app);
    };

    const setWidth = (value: number) => {
        item.width.userSet(Math.max(0, value), {debug: "door-slot-width-edit"});
        emitChanged(app);
    };

    // open the slot's opening in the door workspace. The tool reloads for that opening, so unsaved
    // edits here are lost; ask first.
    const openDoorElevation = async () => {
        if(!opening || !app.project) return;

        if(app.hasUnsavedChanges()) {
            try {
                await confirm.confirm("You have unsaved changes on this elevation. Leave without saving?");
            } catch {
                return; // cancelled
            }
        }

        const project = app.project.id;
        const alternative = app.alternative;
        navigate(alternative != null
            ? `/project/${project}/quote/${alternative}/elevation-tool?quoteOpening=${opening.id}&alternative=${alternative}&workspace=door`
            : `/project/${project}/shop-drawing/elevation-tool?opening=${opening.id}&workspace=door`);
    };

    const remove = () => {
        const name = slot.name;
        if(!removeDoorSlot(spec, name)) {
            app.draw.showError("Can't remove this door slot");
            return;
        }

        const picks = new Map(app.pendingSlotLinks.value);
        picks.delete(name);
        app.pendingSlotLinks.emit(picks);

        emitRebuild(app);
        deselect(app);
    };

    return (
        <>
            <RibbonGroup label="Slot">
                <RibbonStack>
                    <SlotNameField
                        value={slot.name}
                        disabled={savedNames.has(slot.name)}
                        stacked
                        onCommit={to => rename(slot.name, to)}
                    />
                    <RibbonSelect
                        value={opening ? opening.id.toString() : NO_OPENING}
                        options={openingOptions}
                        stacked
                        onChange={pickOpening}
                    />
                </RibbonStack>
            </RibbonGroup>

            <RibbonGroup label="Navigate">
                <RibbonButton
                    icon={<DoorFrontOutlinedIcon />}
                    label="Door Elevation"
                    disabled={!opening}
                    onClick={openDoorElevation}
                />
            </RibbonGroup>

            {/* a filled slot takes its opening's width; edit it on the opening. */}
            <RibbonGroup label={opening ? `Width (from ${opening.name})` : "Width"}>
                <Box ref={widthRef} sx={{display: "flex", height: "100%"}}>
                    <RibbonButton
                        icon={<SwapHorizIcon />}
                        label={app.draw.formatDimension(item.width.get())}
                        disabled={!!opening}
                        onClick={() => setWidthAnchor(widthRef.current)}
                    />
                </Box>
            </RibbonGroup>

            {spec.primarySlot && (
                <RibbonGroup label="Primary Slot Name">
                    <SlotNameField
                        value={spec.primarySlot}
                        disabled={savedNames.has(spec.primarySlot)}
                        onCommit={to => rename(spec.primarySlot!, to)}
                    />
                </RibbonGroup>
            )}

            <RibbonGroup label="Actions">
                <RibbonButton icon={<DeleteOutlineIcon sx={{color: "error.main"}} />} label="Remove Slot" onClick={remove} />
            </RibbonGroup>

            <Popover
                open={!!widthAnchor}
                anchorEl={widthAnchor}
                onClose={() => setWidthAnchor(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            >
                {app.draw.renderDimension({
                    value: item.width.get(),
                    locked: true,
                    onLockChange: () => {},
                    onDone: (value: number) => {
                        setWidthAnchor(null);
                        setWidth(value);
                    },
                    onCancel: () => setWidthAnchor(null),
                })}
            </Popover>
        </>
    );
}

// a slot name, committed on Enter or blur. stacked: inside a RibbonStack (no vertical centering of
// its own).
function SlotNameField(props: {value: string; disabled?: boolean; stacked?: boolean; onCommit(value: string): void}) {
    const [value, setValue] = useState(props.value);
    useEffect(() => setValue(props.value), [props.value]);

    const commit = () => {
        if(value.trim() !== props.value) props.onCommit(value);
        else setValue(props.value);
    };

    return (
        <Box sx={props.stacked ? {display: "flex"} : {display: "flex", alignItems: "center", height: "100%", px: 0.5}}>
            <TextField
                size="small"
                value={value}
                disabled={props.disabled}
                placeholder="Name"
                onChange={e => setValue(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if(e.key === "Enter") commit(); }}
                // stacked: as wide as the opening picker under it.
                sx={{width: props.stacked ? "100%" : 90, "& input": {fontSize: 13, py: props.stacked ? 0.25 : 0.5}}}
            />
        </Box>
    );
}
